import { UserRepository } from '../../domain/repositories/userRepository';
import { USER } from '../../services/apis';
import { api, apiUpload } from '../../services/config';
import { CouponModel, OrderModel, TransactionModel, UserModel, VoucherModel } from '../models/userModel';

/**
 * User Service
 * Implements UserRepository interface with actual API calls
 */
class UserServiceImpl extends UserRepository {
    /**
     * Get user profile
     */
    async getUserProfile() {
        try {
            const response = await api.get(USER.API_USER_PROFILE);
            return UserModel.fromApi(response.data.data || response.data);
        } catch (error) {
            console.error('Error fetching user profile:', error);
            throw error;
        }
    }

    /**
     * Update user profile
     */
    async updateUserProfile(userData) {
        try {
            const apiData = UserModel.toApi(userData);
            const response = await api.put(USER.API_USER_PROFILE, apiData);
            return UserModel.fromApi(response.data.data || response.data);
        } catch (error) {
            console.error('Error updating user profile:', error);
            throw error;
        }
    }

    /**
     * Get credit balance and transaction history
     */
    async getCreditBalance() {
        try {
            const response = await api.get(USER.API_USER_CREDIT);
            const data = response.data.data || response.data;
            return {
                balance: parseFloat(data.balance || data.creditBalance || 0),
                transactions: TransactionModel.fromApiList(data.transactions || data.history || []),
            };
        } catch (error) {
            console.error('Error fetching credit balance:', error);
            throw error;
        }
    }

    /**
     * Get points and points history
     */
    async getPoints() {
        try {
            const response = await api.get(USER.API_USER_POINTS);
            const data = response.data.data || response.data;
            return {
                points: parseInt(data.points || 0),
                transactions: TransactionModel.fromApiList(data.transactions || data.history || []),
            };
        } catch (error) {
            console.error('Error fetching points:', error);
            throw error;
        }
    }

    /**
     * Get user vouchers
     */
    async getVouchers() {
        try {
            const response = await api.get(USER.API_USER_VOUCHERS);
            const data = response.data.data || response.data;
            return VoucherModel.fromApiList(data.vouchers || data);
        } catch (error) {
            console.error('Error fetching vouchers:', error);
            throw error;
        }
    }

    /**
     * Get user coupons
     */
    async getCoupons() {
        try {
            const response = await api.get(USER.API_USER_COUPONS);
            const data = response.data.data || response.data;
            return CouponModel.fromApiList(data.coupons || data);
        } catch (error) {
            console.error('Error fetching coupons:', error);
            throw error;
        }
    }

    /**
     * Get user orders
     */
    async getOrders(params = {}) {
        try {
            const response = await api.get(USER.API_USER_ORDERS, { params });
            const data = response.data.data || response.data;
            return {
                orders: OrderModel.fromApiList(data.orders || data.data || data),
                total: data.total || data.count || 0,
            };
        } catch (error) {
            console.error('Error fetching orders:', error);
            throw error;
        }
    }

    /**
     * Get order by ID
     */
    async getOrderById(orderId) {
        try {
            const response = await api.get(`${USER.API_USER_ORDERS}/${orderId}`);
            return OrderModel.fromApi(response.data.data || response.data);
        } catch (error) {
            console.error('Error fetching order:', error);
            throw error;
        }
    }

    /**
     * Update user password
     */
    async updatePassword(oldPassword, newPassword) {
        try {
            await api.put(`${USER.API_USER_PROFILE}/password`, {
                oldPassword,
                newPassword,
            });
            return true;
        } catch (error) {
            console.error('Error updating password:', error);
            throw error;
        }
    }

    /**
     * Upload user avatar
     */
    async uploadAvatar(file) {
        try {
            const formData = new FormData();
            formData.append('avatar', file);

            const response = await apiUpload.post(`${USER.API_USER_PROFILE}/avatar`, formData);
            const data = response.data.data || response.data;
            return data.avatarUrl || data.url;
        } catch (error) {
            console.error('Error uploading avatar:', error);
            throw error;
        }
    }
}

// Export singleton instance
export const userService = new UserServiceImpl();
